/**
 * NosAKFinanceCarbon — /nosak/finance
 * Institutional Climate Asset Origination & Structuring Infrastructure.
 * Green Finance + Carbon Asset Origination + Scenarios + Project Intelligence.
 * ALL values SSOT-derived from nosak_engine compute_carbon_intelligence().
 */
import React, { useState, useEffect } from "react";
import { useNosak } from "@/context/NOSAKContext";
import client from "@/api/client";
import { toast } from "sonner";
import { Download, Loader, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Lock, Target, Activity } from "lucide-react";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };
const fmtU = (v) => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`; if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`; return `$${n}`; };
const fmtPct = v => `${Number(v || 0).toFixed(1)}%`;

const TABS = [
  { id: "finance",   label: "Green Finance" },
  { id: "carbon",    label: "Carbon Markets" },
  { id: "scenarios", label: "Scenarios" },
  { id: "projects",  label: "Project Ranking" },
];

// Sub-sections within Carbon Markets tab
const CARBON_SECTIONS = [
  { id: "pathways",    label: "Pathway Qualification" },
  { id: "registry",   label: "Registry & Methodology" },
  { id: "article6",   label: "Article 6 Execution" },
  { id: "pdd",        label: "PDD Pipeline" },
  { id: "readiness",  label: "PDD Readiness" },
  { id: "structuring",label: "Asset Structuring" },
  { id: "commercial", label: "Commercialisation" },
];

export default function NosAKFinanceCarbon() {
  const { group, loading, selectedPeriod } = useNosak();
  const [activeTab, setActiveTab] = useState("finance");
  const [activeCarbon, setActiveCarbon] = useState("pathways");
  const [analysis, setAnalysis] = useState(null);
  const [qualData, setQualData] = useState(null);
  const [projData, setProjData] = useState([]);
  const [carbonIntel, setCarbonIntel] = useState(null);
  const [loadingCarbon, setLoadingCarbon] = useState(false);
  const [expandedPath, setExpandedPath] = useState(null);
  const [expandedPDD, setExpandedPDD] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [debtPct, setDebtPct] = useState(60);
  const [equityPct, setEquityPct] = useState(25);
  const [concPct, setConcPct] = useState(15);
  const [tenor, setTenor] = useState(7);
  const [fetching, setFetching] = useState(false);

  const fetchAll = async () => {
    if (!selectedPeriod) return;
    setFetching(true);
    try {
      const [finR, qualR, projR] = await Promise.all([
        client.get(`/nosak/financing/analysis/${selectedPeriod}?debt_pct=${debtPct}&equity_pct=${equityPct}&conc_pct=${concPct}&tenor_yr=${tenor}`),
        client.get(`/nosak/qualification/${selectedPeriod}`),
        client.get(`/nosak/project-analytics/${selectedPeriod}`),
      ]);
      setAnalysis(finR.data);
      setQualData(qualR.data);
      setProjData(projR.data?.projects || []);
    } catch { toast.error("Data load failed"); }
    finally { setFetching(false); }
  };

  // Load carbon intelligence when switching to carbon tab
  const loadCarbonIntel = async () => {
    if (!selectedPeriod || carbonIntel) return;
    setLoadingCarbon(true);
    try {
      const r = await client.get(`/nosak/carbon-intelligence/${selectedPeriod}`);
      setCarbonIntel(r.data);
    } catch { toast.error("Carbon intelligence load failed"); }
    finally { setLoadingCarbon(false); }
  };

  useEffect(() => { fetchAll(); }, [selectedPeriod]);

  // Auto-load carbon intelligence when carbon tab is active
  useEffect(() => {
    if (activeTab === "carbon") loadCarbonIntel();
  }, [activeTab, selectedPeriod]);

  const downloadPdf = async (type) => {
    setDownloading(type);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nosak/pdf/${type}/${selectedPeriod}?debt_pct=${debtPct}&equity_pct=${equityPct}&conc_pct=${concPct}&tenor_yr=${tenor}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` } }
      );
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `Nosak_${type}_${selectedPeriod}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(null); }
  };

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const cap = analysis?.capital_stack || {};
  const fin = analysis?.financials || {};
  const fin_g = group.sustainable_finance || {};
  const qs = qualData?.group_qualification || {};
  const total_red = group.reduction_potential_tco2e || 0;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">Nosak Group</span>
        <h1 className="text-2xl font-black text-slate-900 mt-1.5">Sustainable Finance & Carbon Markets</h1>
        <p className="text-xs text-slate-400 mt-0.5">Capital structuring · Carbon qualification · Transition scenarios · Project prioritisation</p>
      </div>

      {/* Capital Stack Config */}
      <div className="bg-[#0f172a] rounded-xl p-5 text-white">
        <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-3">Capital Stack Configuration</p>
        <div className="grid grid-cols-4 gap-4 mb-3">
          {[["Senior Debt", debtPct, setDebtPct, "#60a5fa"], ["Equity", equityPct, setEquityPct, "#34d399"], ["Concessional", concPct, setConcPct, "#c4b5fd"]].map(([l, v, s, c]) => (
            <div key={l}><p className="text-[9px] opacity-40 mb-1">{l}</p>
              <input type="range" min={0} max={100} value={v} onChange={e => s(Number(e.target.value))} className="w-full h-1.5 rounded-full" style={{ accentColor: c }} />
              <p className="text-sm font-black mt-1" style={{ color: c }}>{v}%</p>
            </div>
          ))}
          <div><p className="text-[9px] opacity-40 mb-1">Bond Tenor</p>
            <div className="flex gap-1">{[5, 7, 10, 15].map(t => (
              <button key={t} onClick={() => setTenor(t)} className={`text-[10px] px-2 py-1 rounded font-bold ${tenor === t ? "bg-white text-slate-900" : "text-white/50 hover:text-white"}`}>{t}yr</button>
            ))}</div>
          </div>
        </div>
        <button onClick={fetchAll} disabled={fetching}
          className="text-xs bg-emerald-700 text-white px-4 py-2 rounded-lg font-bold hover:bg-emerald-600 disabled:opacity-60"
          data-testid="recalculate-btn">{fetching ? "Recalculating…" : "Recalculate"}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1.5">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all ${activeTab === t.id ? "bg-[#0f172a] text-white" : "text-slate-500 hover:bg-slate-50"}`}
            data-testid={`tab-${t.id}`}>{t.label}
          </button>
        ))}
      </div>

      {/* GREEN FINANCE */}
      {activeTab === "finance" && analysis && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              ["Total CAPEX Pipeline", fmtU(cap.total_capex), "8 projects"],
              ["ICMA-Eligible CAPEX", fmtU(cap.eligible_capex), `${fin_g.icma_eligible_pct || 0}% eligible`],
              ["Indicative Bond Size", fmtU(cap.bond_size), `${debtPct}% debt tranche`],
              ["Operational Savings", fmtU(fin.operational_value_usd_yr) + "/yr", "$15/tCO₂e conservative"],
              ["Concessional Tranche", fmtU(cap.concessional), "DFI / blended finance"],
              ["Equity Contribution", fmtU(cap.equity), "Co-investment"],
            ].map(([k, v, s]) => (
              <div key={k} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] text-slate-400">{k}</p>
                <p className="text-xl font-black mt-1 text-slate-800">{v}</p>
                <p className="text-[9px] text-slate-400 mt-0.5">{s}</p>
              </div>
            ))}
          </div>
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Operational DSCR Sensitivity — {tenor}yr Selected (Carbon Excluded)</p>
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 border-b border-slate-100">
                {["Tenor", "Annual Debt Service", "DSCR Operational", "DSCR with Carbon (sensitivity)"].map(h => (
                  <th key={h} className="text-left py-2 px-3 text-slate-500 font-semibold text-[11px]">{h}</th>
                ))}
              </tr></thead>
              <tbody>{Object.values(fin.dscr_sensitivity || {}).map(row => (
                <tr key={row.tenor_yr} className={`border-b border-slate-50 ${row.tenor_yr === tenor ? "bg-blue-50" : ""}`} data-testid={`dscr-row-${row.tenor_yr}`}>
                  <td className="py-2 px-3 font-black">{row.tenor_yr}yr{row.tenor_yr === tenor && <span className="ml-1 text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded">Selected</span>}</td>
                  <td className="py-2 px-3">{fmtU(row.annual_debt_service)}/yr</td>
                  <td className="py-2 px-3 font-black" style={{ color: row.dscr_op >= 1.2 ? "#059669" : "#d97706" }}>{row.dscr_op}x</td>
                  <td className="py-2 px-3 text-slate-400">{row.dscr_with_carbon}x <span className="text-[9px]">(sensitivity)</span></td>
                </tr>
              ))}</tbody>
            </table>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3 text-xs text-amber-700 flex items-start gap-2">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
              <span>Carbon excluded from base DSCR per ICMA GBP 2021. Carbon value NOT bankable until VVB-validated and registry-issued.</span>
            </div>
          </div>
          {/* PDFs */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Institutional Reports</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "sustainable-finance", label: "Sustainable Finance", badge: "ICMA GBP 2021" },
                { key: "executive", label: "Executive Intelligence", badge: "Board Report" },
                { key: "carbon-monetisation", label: "Carbon Monetisation", badge: "VCM · Art.6" },
              ].map(r => (
                <button key={r.key} onClick={() => downloadPdf(r.key)} disabled={downloading === r.key}
                  className="flex items-center justify-center gap-2 bg-[#1d4ed8] text-white text-xs px-3 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-60 font-bold"
                  data-testid={`download-${r.key}`}>
                  {downloading === r.key ? <><Loader size={11} className="animate-spin" /> Generating…</> : <><Download size={11} /> {r.label}</>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CARBON MARKETS — Institutional Climate Asset Origination Workspace */}
      {activeTab === "carbon" && (
        <div className="space-y-4">

          {/* Loading State */}
          {loadingCarbon && (
            <div className="bg-[#0f172a] rounded-xl p-8 flex items-center justify-center gap-3">
              <Loader size={18} className="animate-spin text-emerald-400" />
              <p className="text-xs text-slate-400">Loading carbon intelligence…</p>
            </div>
          )}

          {/* No-data fallback */}
          {!carbonIntel && !loadingCarbon && (
            <div className="bg-[#0f172a] rounded-xl p-8 text-center border border-slate-700/60">
              <p className="text-xs text-slate-400">No carbon intelligence loaded. Select a period or switch tabs to trigger load.</p>
            </div>
          )}

          {/* ── SSOT SIGNAL BAR ─────────────────────────────────────────── */}
          {carbonIntel && (
            <div className="bg-[#0f172a] rounded-xl p-4 border border-slate-700/60" data-testid="ssot-signal-bar">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500">SSOT Signals — Carbon Intelligence Derivation</span>
                <div className="flex gap-1.5">
                  <span className="text-[9px] bg-amber-900/40 text-amber-400 border border-amber-700/40 px-2 py-0.5 rounded font-bold">Indicative Only</span>
                  <span className="text-[9px] bg-slate-700/60 text-slate-300 px-2 py-0.5 rounded font-bold">Not Bankable</span>
                </div>
              </div>
              <div className="grid grid-cols-6 gap-3">
                {[
                  ["Data Confidence", `${(carbonIntel.ssot_signals?.conf_avg||0).toFixed(0)}/100`, (carbonIntel.ssot_signals?.conf_avg||0)>=65?"#059669":(carbonIntel.ssot_signals?.conf_avg||0)>=50?"#d97706":"#dc2626", "VVB threshold \u226565"],
                  ["T1/T2 Coverage", `${carbonIntel.ssot_signals?.t1_share_pct||0}%`, (carbonIntel.ssot_signals?.t1_share_pct||0)>=70?"#059669":"#d97706", "Target \u226570%"],
                  ["Monetisable Vol.", fmtN(carbonIntel.ssot_signals?.monetisable,"tCO\u2082e"), "#60a5fa", "Subject to validation"],
                  ["VCM Projects", `${carbonIntel.ssot_signals?.vcm_projects||0}`, "#059669", "Pathway assigned"],
                  ["Art.6 Projects", `${carbonIntel.ssot_signals?.art6_projects||0}`, "#7c3aed", "Host auth. required"],
                  ["Methane Capture", carbonIntel.ssot_signals?.has_methane?"Identified":"Not identified", carbonIntel.ssot_signals?.has_methane?"#059669":"#d97706", "Priority opportunity"],
                ].map(([label, val, color, note]) => (
                  <div key={label} className="border border-slate-700/50 rounded-lg p-2.5">
                    <p className="text-[8px] uppercase tracking-wide text-slate-500 mb-1">{label}</p>
                    <p className="text-sm font-black" style={{ color }}>{val}</p>
                    <p className="text-[8px] text-slate-600 mt-0.5">{note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Carbon Section Sub-Nav */}
          {carbonIntel && (
            <div className="flex gap-1 flex-wrap bg-[#0f172a] border border-slate-700/60 rounded-xl p-1.5" data-testid="carbon-subnav">
              {CARBON_SECTIONS.map(s => (
                <button key={s.id} onClick={() => setActiveCarbon(s.id)}
                  className={`py-1.5 px-3 rounded-lg text-[10px] font-semibold transition-all ${activeCarbon===s.id?"bg-emerald-800/60 text-emerald-300 border border-emerald-700/50":"text-slate-500 hover:text-slate-300 hover:bg-slate-800/50"}`}
                  data-testid={`carbon-section-${s.id}`}>{s.label}
                </button>
              ))}
            </div>
          )}

          {/* ── SECTION 1: PATHWAY QUALIFICATION ENGINE ─────────────────── */}
          {carbonIntel && activeCarbon === "pathways" && (
            <div className="space-y-3" data-testid="section-pathways">
              <div className="bg-[#0f172a] rounded-xl p-4 border border-slate-700/60">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Pathway Qualification Engine</p>
                <p className="text-[10px] text-slate-400">Five origination pathways assessed against SSOT evidence quality, sector methodology availability, and host-country regulatory context. Readiness scores SSOT-derived from data confidence and evidence levels.</p>
              </div>
              {(carbonIntel.pathway_qualification||[]).map((pw) => (
                <div key={pw.short} className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden" data-testid={`pathway-card-${pw.short}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-black text-slate-800">{pw.name}</span>
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded border" style={{ background: pw.accent+"18", borderColor: pw.accent+"40", color: pw.accent }}>{pw.eligibility}</span>
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${pw.status==="Active"?"bg-emerald-50 text-emerald-700":pw.status==="Recommended"?"bg-blue-50 text-blue-700":pw.status==="Early Stage"?"bg-amber-50 text-amber-700":"bg-slate-100 text-slate-500"}`}>{pw.status}</span>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${pw.confidence_level==="High"?"text-emerald-600 bg-emerald-50":pw.confidence_level==="Medium"?"text-blue-600 bg-blue-50":"text-slate-500 bg-slate-100"}`}>{pw.confidence_level} confidence</span>
                        </div>
                        <p className="text-[10px] text-slate-500 leading-relaxed">{pw.basis}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <p className="text-[8px] text-slate-400 mb-0.5">Indicative Volume</p>
                        <p className="text-sm font-black" style={{ color: pw.accent }}>{fmtN(pw.volume_tco2e,"tCO\u2082e")}</p>
                        <p className="text-[8px] text-slate-400">~{pw.timeline_months}mo to issuance</p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[9px] font-semibold text-slate-500">Pathway Readiness</p>
                        <span className="text-[9px] font-black" style={{ color: pw.accent }}>{pw.readiness_pct}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pw.readiness_pct}%`, background: pw.accent }} />
                      </div>
                      <div className="flex justify-between mt-0.5">
                        <span className="text-[8px] text-slate-400">0%</span>
                        <span className="text-[8px] text-amber-500 font-medium">65% VVB threshold</span>
                        <span className="text-[8px] text-slate-400">100%</span>
                      </div>
                    </div>
                    <button onClick={() => setExpandedPath(expandedPath===pw.short?null:pw.short)}
                      className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-700 font-medium"
                      data-testid={`pathway-expand-${pw.short}`}>
                      {expandedPath===pw.short?<ChevronUp size={11}/>:<ChevronDown size={11}/>}
                      {expandedPath===pw.short?"Hide":"Show"} blockers & required actions ({pw.blockers?.length||0})
                    </button>
                  </div>
                  {expandedPath===pw.short && (
                    <div className="bg-[#0f172a] px-4 py-3 border-t border-slate-700/60">
                      <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-2">Blockers & Required Actions</p>
                      <div className="space-y-1.5 mb-3">
                        {(pw.blockers||[]).map((b,bi) => (
                          <div key={bi} className="flex items-start gap-2">
                            <AlertTriangle size={10} className="text-amber-400 flex-shrink-0 mt-0.5"/>
                            <p className="text-[10px] text-slate-300">{b}</p>
                          </div>
                        ))}
                      </div>
                      {(pw.short==="Art.6.2"||pw.short==="Art.6.4") && (
                        <div className="bg-red-900/30 border border-red-700/40 rounded-lg p-3">
                          <p className="text-[9px] font-bold text-red-400 mb-1">Sovereign Process Mandatory</p>
                          <p className="text-[10px] text-red-300 leading-relaxed">NOT financeable until NCCC (Nigeria) formally authorises. Requires host country authorisation, corresponding adjustment review, bilateral framework establishment, and DNA engagement. Exclude from all debt structuring and DSCR models until sovereign authorisation is granted.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── SECTION 2: REGISTRY & METHODOLOGY ALIGNMENT ─────────────── */}
          {carbonIntel && activeCarbon === "registry" && (
            <div className="space-y-3" data-testid="section-registry">
              <div className="bg-[#0f172a] rounded-xl p-4 border border-slate-700/60">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Registry & Methodology Alignment</p>
                <p className="text-[10px] text-slate-400">Each carbon-eligible project mapped to primary registry and methodology family. Compatibility derived from project status, evidence quality, and SSOT confidence. Formal methodology pre-screening by accredited DOE required before PDD development.</p>
              </div>
              <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-x-auto">
                <table className="w-full text-xs" style={{ minWidth: 900 }}>
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {["Project","Registry","Methodology","Family","Stage","Compat.","Pathway","Next Required Action"].map(h => (
                        <th key={h} className="text-left py-2.5 px-3 text-slate-500 font-semibold text-[10px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(carbonIntel.registry_methodology||[]).map((r,i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`registry-row-${i}`}>
                        <td className="py-2.5 px-3 font-medium text-[10px] max-w-[130px]"><p className="truncate">{r.project}</p></td>
                        <td className="py-2.5 px-3 text-[10px] font-bold text-blue-700 whitespace-nowrap">{r.registry}</td>
                        <td className="py-2.5 px-3 text-[10px] text-slate-600 whitespace-nowrap">{r.methodology}</td>
                        <td className="py-2.5 px-3 text-[10px] text-slate-500">{r.family}</td>
                        <td className="py-2.5 px-3"><span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium text-[9px] whitespace-nowrap">{r.stage}</span></td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-10 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${r.compat_score}%`, background: r.compat_color }}/>
                            </div>
                            <span className="text-[10px] font-black" style={{ color: r.compat_color }}>{r.compat_score}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${r.pathway==="art6"?"bg-purple-50 text-purple-700":r.pathway==="vcm"?"bg-emerald-50 text-emerald-700":"bg-slate-100 text-slate-500"}`}>
                            {r.pathway==="art6"?"Art.6":r.pathway==="vcm"?"VCM":"—"}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-[10px] text-blue-700 font-medium">
                          {r.compat_score>=70?"Commission baseline study & DOE pre-screening":r.compat_score>=45?"Upgrade evidence quality; commission methodology review":"Formal methodology review required before PDD"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
                  <p className="text-[10px] text-amber-700"><span className="font-bold">Methodology caveat:</span> Compatibility scores are indicative. Formal pre-screening by an accredited DOE required before registry engagement or PDD development. Missing T1 metering and baseline studies are the primary evidence blockers.</p>
                </div>
              </div>
            </div>
          )}

          {/* ── SECTION 3: ARTICLE 6 EXECUTION MATRIX ───────────────────── */}
          {carbonIntel && activeCarbon === "article6" && (
            <div className="space-y-3" data-testid="section-article6">
              <div className="bg-[#0f172a] rounded-xl p-4 border border-slate-700/60">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Article 6 Execution Matrix — Early Exploratory Stage Only</p>
                    <p className="text-[10px] text-slate-400">Sovereign and regulatory pre-conditions for Art.6.2 bilateral and Art.6.4 PACM access assessed against current Nosak Group institutional status.</p>
                  </div>
                  <div className="text-right ml-4 flex-shrink-0">
                    <p className="text-[8px] text-slate-500 mb-0.5">Overall Readiness</p>
                    <p className="text-2xl font-black text-amber-400">{carbonIntel.article6_overall}/100</p>
                    <p className="text-[9px] text-amber-500 font-semibold">Early Stage Only</p>
                  </div>
                </div>
              </div>
              <div className="bg-red-900/20 border border-red-700/40 rounded-xl p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={14} className="text-red-400 flex-shrink-0 mt-0.5"/>
                  <div>
                    <p className="text-xs font-bold text-red-300 mb-1">Critical Institutional Caveat — Article 6 Pathways</p>
                    <p className="text-[10px] text-red-300 leading-relaxed">Art.6.2 and Art.6.4 pathways are NOT financeable, NOT bankable, and NOT commitable until: (a) NCCC (Nigeria) sovereign authorisation formally granted; (b) corresponding adjustment reviewed and approved by host country; (c) bilateral host-country framework established; (d) DNA engagement completed; (e) ITMO framework formally agreed. Do NOT include Article 6 carbon values in financing, DSCR, or valuation models without formal sovereign authorisation.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-x-auto">
                <table className="w-full text-xs" style={{ minWidth: 950 }}>
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {["Requirement","Status","Readiness","Assessment Notes","Next Required Action","Who Must Act"].map(h => (
                        <th key={h} className="text-left py-2.5 px-3 text-slate-500 font-semibold text-[10px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(carbonIntel.article6_matrix||[]).map((row,i) => (
                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`art6-row-${i}`}>
                        <td className="py-2.5 px-3 font-semibold text-[10px] text-slate-800 max-w-[150px]">{row.requirement}</td>
                        <td className="py-2.5 px-3">
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded whitespace-nowrap" style={{ background: row.color+"18", color: row.color }}>{row.status}</span>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${row.score}%`, background: row.color }}/>
                            </div>
                            <span className="text-[10px] font-black" style={{ color: row.color }}>{row.score}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-[10px] text-slate-500 max-w-[220px]">{row.note}</td>
                        <td className="py-2.5 px-3 text-[10px] text-blue-700 font-medium max-w-[160px]">
                          {row.score<20?"Immediate: Initiate DNA & NCCC engagement":row.score<40?"Commission formal alignment assessment":row.score<60?"Formalise bilateral framework":"Advance to ITMO implementation"}
                        </td>
                        <td className="py-2.5 px-3 text-[10px] text-slate-500 whitespace-nowrap">
                          {(row.requirement||"").includes("DNA")?"Nigeria DNA / NCCC":(row.requirement||"").includes("Host")?"Nigeria NCCC":(row.requirement||"").includes("Bilateral")?"NCCC + Buyer-country":(row.requirement||"").includes("NDC")?"Fed. Ministry + Nosak":"NCCC / Supervisory Body"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                  <p className="text-[10px] text-slate-500"><span className="font-bold">Timeline guidance:</span> DNA engagement → 3–6 months. Bilateral framework → 12–24 months post-DNA. NCCC authorisation → 18–36 months. ITMO agreement → 24–48 months. Full Art.6 pathway not viable before 2029 at earliest.</p>
                </div>
              </div>
            </div>
          )}

          {/* ── SECTION 4: PDD DEVELOPMENT PIPELINE ─────────────────────── */}
          {carbonIntel && activeCarbon === "pdd" && (
            <div className="space-y-3" data-testid="section-pdd">
              <div className="bg-[#0f172a] rounded-xl p-4 border border-slate-700/60">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">PDD Development Pipeline — 11-Stage Institutional Workflow</p>
                <p className="text-[10px] text-slate-400">Project Design Document advancement tracker for carbon-eligible projects. Stage progression derived from project status and evidence quality. Missing T1 metering, incomplete baseline studies, and weak additionality evidence directly block stage advancement.</p>
              </div>
              {(carbonIntel.pdd_pipeline||[]).length===0 && (
                <div className="bg-white border border-slate-100 rounded-xl p-6 text-center">
                  <p className="text-xs text-slate-400">No carbon-pathway projects in pipeline. Assign carbon_pathway to eligible projects in the Data Workspace.</p>
                </div>
              )}
              {(carbonIntel.pdd_pipeline||[]).map((p) => (
                <div key={p.project_id} className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden" data-testid={`pdd-card-${p.project_id}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-xs font-black text-slate-800">{p.name}</span>
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${p.pathway==="art6"?"bg-purple-50 text-purple-700 border-purple-200":"bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                            {p.pathway==="art6"?"Art.6 — Sovereign Auth. Required":"VCM"}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-500">Stage {p.current_stage}/10: <span className="font-semibold text-slate-700">{p.stage_label}</span> · Readiness: {p.readiness_pct}%</p>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <p className="text-[8px] text-slate-400">Issuance Potential</p>
                        <p className="text-sm font-black text-blue-700">{fmtN(p.issue_potential_tco2e,"tCO\u2082e")}</p>
                        <p className="text-[9px] text-slate-400">{p.timeline_yr}yr to issuance</p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="flex gap-0.5 mb-1">
                        {(p.all_stages||[]).map((st,si) => (
                          <div key={si} className="flex-1 group relative cursor-default">
                            <div className={`h-2 rounded-sm ${si<p.current_stage?"bg-emerald-500":si===p.current_stage?"bg-blue-500":"bg-slate-100"}`}/>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block bg-slate-900 text-white text-[8px] px-2 py-1 rounded whitespace-nowrap z-20 pointer-events-none shadow-lg">{si+1}. {st}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[8px] text-slate-400">Opportunity ID</span>
                        <span className="text-[8px] text-blue-500 font-semibold">{p.stage_label}</span>
                        <span className="text-[8px] text-slate-400">Monetisation</span>
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 mb-2.5">
                      <p className="text-[9px] font-bold text-blue-700 mb-0.5">Next Required Action</p>
                      <p className="text-[10px] text-blue-800 leading-relaxed">{p.next_action}</p>
                    </div>
                    <button onClick={() => setExpandedPDD(expandedPDD===p.project_id?null:p.project_id)}
                      className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-700 font-medium"
                      data-testid={`pdd-expand-${p.project_id}`}>
                      {expandedPDD===p.project_id?<ChevronUp size={11}/>:<ChevronDown size={11}/>}
                      {expandedPDD===p.project_id?"Hide":"Show"} evidence & process blockers ({p.blockers?.length||0})
                    </button>
                  </div>
                  {expandedPDD===p.project_id && (
                    <div className="bg-[#0f172a] px-4 py-3 border-t border-slate-700/60">
                      <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-2">Evidence & Process Blockers</p>
                      <div className="space-y-1.5">
                        {(p.blockers||[]).map((b,bi) => (
                          <div key={bi} className="flex items-start gap-2">
                            <AlertTriangle size={10} className="text-amber-400 flex-shrink-0 mt-0.5"/>
                            <p className="text-[10px] text-slate-300">{b}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── SECTION 5: PDD READINESS SCORES ─────────────────────────── */}
          {carbonIntel && activeCarbon === "readiness" && (
            <div className="space-y-3" data-testid="section-readiness">
              <div className="bg-[#0f172a] rounded-xl p-4 border border-slate-700/60">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">PDD Readiness Assessment — Six-Dimension Framework</p>
                    <p className="text-[10px] text-slate-400">Baseline, additionality, monitoring, leakage, permanence, and MRV maturity. All scores SSOT-derived from evidence levels and data confidence. 65/100 threshold required for VVB pre-qualification.</p>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-[8px] text-slate-500 mb-0.5">Overall Score</p>
                      <p className="text-2xl font-black" style={{ color: carbonIntel.pdd_overall_score>=65?"#059669":carbonIntel.pdd_overall_score>=50?"#d97706":"#dc2626" }}>{carbonIntel.pdd_overall_score}/100</p>
                    </div>
                    <div className={`px-3 py-2 rounded-lg border text-center ${carbonIntel.pdd_validation_ready?"bg-emerald-900/30 border-emerald-700/40":"bg-amber-900/30 border-amber-700/40"}`}>
                      <p className="text-[8px] text-slate-500 mb-0.5">VVB Pre-qual.</p>
                      <p className={`text-[10px] font-black ${carbonIntel.pdd_validation_ready?"text-emerald-400":"text-amber-400"}`}>{carbonIntel.pdd_validation_ready?"Ready":"Not ready"}</p>
                      <p className="text-[8px] text-slate-400">Prob.: {carbonIntel.issuance_probability}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(carbonIntel.pdd_readiness||{}).map(([key,dim]) => (
                  <div key={key} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm" data-testid={`readiness-dim-${key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-slate-700">{dim.label}</p>
                      <span className="text-sm font-black" style={{ color: dim.score>=65?"#059669":dim.score>=45?"#d97706":"#dc2626" }}>{dim.score}/100</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2">
                      <div className="h-full rounded-full" style={{ width: `${dim.score}%`, background: dim.score>=65?"#059669":dim.score>=45?"#d97706":"#dc2626" }}/>
                    </div>
                    <p className="text-[10px] text-slate-500 leading-relaxed">{dim.note}</p>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs font-bold text-amber-700 mb-1">VVB Pre-qualification Threshold</p>
                <p className="text-[10px] text-amber-700">Overall PDD readiness ≥65/100 required for VVB pre-qualification screening. Current: <strong>{carbonIntel.pdd_overall_score}/100</strong>. Priority actions: upgrade estimated-level subsidiaries to measured/declared, commission independent baseline study, and draft monitoring plan including POME volume baseline and production records.</p>
              </div>
            </div>
          )}

          {/* ── SECTION 6: CARBON ASSET STRUCTURING ─────────────────────── */}
          {carbonIntel && activeCarbon === "structuring" && (
            <div className="space-y-3" data-testid="section-structuring">
              <div className="bg-[#0f172a] rounded-xl p-4 border border-slate-700/60">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Carbon Asset Structuring — Revenue Architecture</p>
                <p className="text-[10px] text-slate-400">Revenue waterfall, SPV viability, forward offtake feasibility, and blended finance pre-conditions. All values indicative and SSOT-derived. Carbon revenue excluded from base-case DSCR per ICMA GBP 2021.</p>
              </div>
              <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <p className="text-xs font-bold text-slate-700">Annual Revenue Waterfall — Base Case ($18/tCO₂e, indicative, not bankable)</p>
                </div>
                <div className="p-4 space-y-2">
                  {(carbonIntel.structuring?.revenue_waterfall||[]).map((row,i) => {
                    const isNet = (row[0]||"").includes("Net");
                    const grossVal = Math.abs(((carbonIntel.structuring?.revenue_waterfall||[])[0]||[])[1]||1);
                    const barW = Math.min(100, Math.abs(row[1]||0)/grossVal*100);
                    return (
                      <div key={i} className={`flex items-center justify-between py-2 ${isNet?"border-t-2 border-slate-200 mt-2 pt-3":""}`} data-testid={`waterfall-row-${i}`}>
                        <span className={`text-xs ${isNet?"font-black text-slate-800":"text-slate-600"}`}>{row[0]}</span>
                        <div className="flex items-center gap-3">
                          <div className="w-28 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${barW}%`, background: row[2]||"#94a3b8" }}/>
                          </div>
                          <span className="text-xs font-black w-24 text-right" style={{ color: row[2]||"#94a3b8" }}>
                            {(row[1]||0)>=0?"+":""}{fmtU(Math.abs(row[1]||0))}/yr
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
                  <p className="text-[10px] text-blue-700"><span className="font-bold">5-Year Net Revenue (indicative):</span> {fmtU(carbonIntel.structuring?.net_revenue_5yr)} · Verification cost: ${carbonIntel.structuring?.verification_cost_tco2e}/tCO₂e · Subject to full validation pathway completion.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["SPV Readiness", carbonIntel.structuring?.spv_readiness],
                  ["Forward Offtake", carbonIntel.structuring?.forward_offtake],
                  ["Blended Finance", carbonIntel.structuring?.blended_finance],
                  ["Insurance Requirement", carbonIntel.structuring?.insurance_req],
                ].map(([label, item]) => item ? (
                  <div key={label} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm" data-testid={`struct-${label.toLowerCase().replace(/\s/g,"-")}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-slate-700">{label}</p>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${item.status==="Ready"||item.status==="Applicable"?"bg-emerald-50 text-emerald-700":item.status==="Feasible"||item.status==="Required"?"bg-blue-50 text-blue-700":item.status==="Conditional"?"bg-amber-50 text-amber-700":"bg-slate-100 text-slate-600"}`}>{item.status}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-2">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${item.score}%` }}/>
                    </div>
                    <p className="text-[10px] text-slate-500 leading-relaxed">{item.note}</p>
                  </div>
                ) : null)}
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-xs text-red-700">
                <p className="font-bold mb-1">Carbon Revenue Exclusion from DSCR</p>
                <p className="leading-relaxed">Carbon revenue is excluded from base-case DSCR per ICMA GBP 2021. Values above are indicative only. Carbon revenue is NOT bankable until: (a) VVB-validated against approved methodology, (b) formally registered and issued by approved registry, (c) successfully transacted. Any sensitivity analysis including carbon revenue must be clearly labelled as non-bankable upside.</p>
              </div>
            </div>
          )}

          {/* ── SECTION 7: COMMERCIALISATION PATHWAYS ────────────────────── */}
          {carbonIntel && activeCarbon === "commercial" && (
            <div className="space-y-3" data-testid="section-commercial">
              <div className="bg-[#0f172a] rounded-xl p-4 border border-slate-700/60">
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">Commercialisation & Buyer Pathway Intelligence</p>
                <p className="text-[10px] text-slate-400">Buyer alignment and commercialisation structure by project type. Priority derived from market demand, pathway type, and SSOT readiness. Indicative values only — no binding offtake agreements exist.</p>
              </div>
              {(carbonIntel.commercialisation||[]).length===0 && (
                <div className="bg-white border border-slate-100 rounded-xl p-6 text-center">
                  <p className="text-xs text-slate-400">No commercialisation data. Ensure projects have carbon_pathway assigned in the Data Workspace.</p>
                </div>
              )}
              {(carbonIntel.commercialisation||[]).length>0 && (
                <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-x-auto">
                  <table className="w-full text-xs" style={{ minWidth: 1050 }}>
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        {["Project","Target Buyer","Market","Structure","Priority","Readiness","Volume","Indicative Value","Next Required Action"].map(h => (
                          <th key={h} className="text-left py-2.5 px-3 text-slate-500 font-semibold text-[10px]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(carbonIntel.commercialisation||[]).map((c,i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`commercial-row-${i}`}>
                          <td className="py-2.5 px-3 font-medium text-[10px] max-w-[120px]"><p className="truncate">{c.project}</p></td>
                          <td className="py-2.5 px-3 text-[10px] text-slate-600">{c.buyer}</td>
                          <td className="py-2.5 px-3">
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${c.market.includes("Art")?"bg-purple-50 text-purple-700":c.market.includes("Insetting")?"bg-blue-50 text-blue-700":c.market.includes("Plastic")?"bg-amber-50 text-amber-700":"bg-emerald-50 text-emerald-700"}`}>{c.market}</span>
                          </td>
                          <td className="py-2.5 px-3 text-[10px] text-slate-500">{c.structure}</td>
                          <td className="py-2.5 px-3">
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${c.priority==="High"?"bg-emerald-50 text-emerald-700":c.priority==="Medium"?"bg-amber-50 text-amber-700":"bg-slate-100 text-slate-500"}`}>{c.priority}</span>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${c.readiness==="Ready"?"bg-emerald-50 text-emerald-700":"bg-amber-50 text-amber-700"}`}>{c.readiness}</span>
                          </td>
                          <td className="py-2.5 px-3 font-bold text-[10px] text-blue-700">{fmtN(c.volume_tco2e)}</td>
                          <td className="py-2.5 px-3 text-[10px]">
                            <p className="font-black text-slate-700">{fmtU(c.indicative_value)}/yr</p>
                            <p className="text-[9px] text-slate-400">indicative only</p>
                          </td>
                          <td className="py-2.5 px-3 text-[10px] text-blue-700 font-medium">
                            {c.readiness==="Ready"?"Initiate DOE pre-screening & buyer engagement":"Upgrade evidence to T1/T2 before buyer outreach"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
                    <p className="text-[10px] text-amber-700"><span className="font-bold">Commercialisation caveat:</span> Buyer alignment is indicative only. No binding offtake agreements exist. Forward sales require completed PDD, VVB validation, and registry issuance. Values based on $18/tCO₂e conservative assumption. Missing DOE pre-screen and absence of monitoring plan are current blockers.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mandatory Disclaimer — always visible in Carbon tab */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-xs text-red-700" data-testid="carbon-mandatory-disclaimer">
            <p className="font-bold mb-1">Mandatory Carbon Asset Origination Disclosure</p>
            <p className="leading-relaxed">{qualData?.mandatory_disclaimer||"Carbon monetisation values are INDICATIVE ESTIMATES based on SSOT reduction potential at conservative market prices. They are NOT guaranteed or committed revenues until: (a) accredited methodology registered and approved, (b) additionality independently demonstrated, (c) VVB validated against approved methodology, (d) carbon credits formally issued by approved registry (Verra VCS, Gold Standard, or NCCC), (e) credits successfully transacted. Article 6.4 conditional on NCCC sovereign authorisation. Not for use in financing commitments without formal validation."}</p>
          </div>
        </div>
      )}

      {/* SCENARIOS */}
      {activeTab === "scenarios" && (
        <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
            <p className="text-xs font-bold text-slate-700">Transition Scenario Analysis — Nigerian Industrial Context</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Conservative assumptions reflecting Nigerian infrastructure, CAPEX constraints, and fuel availability</p>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Scenario", "2030 Emissions", "Key Assumptions", "CAPEX Required", "Scope 1+2 Change", "Risk Level"].map(h => (
                <th key={h} className="text-left py-2.5 px-4 text-slate-500 font-semibold text-[11px]">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {[
                ["BAU (No transition)", fmtN((group.total_tco2e || 0) * 1.15, "tCO₂e/yr"), "Growth-led, no investment", "$0", "+12–18%", "Regulatory / reputational"],
                ["Moderate Transition", fmtN((group.total_tco2e || 0) - (group.reduction_potential_tco2e || 0) * 0.40, "tCO₂e/yr"), "40% feasible reduction, partial HFO switch", "$18–25M", "-25–35%", "Medium — supply chain"],
                ["Accelerated Transition", fmtN((group.total_tco2e || 0) - (group.reduction_potential_tco2e || 0) * 0.66, "tCO₂e/yr"), "66% feasible reduction, biomass + solar", "$35–45M", "-45–55%", "High — CAPEX + timeline"],
                ["Net-Zero 2040 (Scope 1+2)", "Near-zero operational", "Full HFO exit + renewables + efficiency", "$50M+", "~-95% Scope 1+2", "Very High — infrastructure"],
              ].map(([s, e, a, c, r, risk]) => (
                <tr key={s} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2.5 px-4 font-bold text-slate-800">{s}</td>
                  <td className="py-2.5 px-4">{e}</td>
                  <td className="py-2.5 px-4 text-slate-500 text-[10px]">{a}</td>
                  <td className="py-2.5 px-4 font-bold text-blue-700">{c}</td>
                  <td className="py-2.5 px-4 font-bold text-emerald-700">{r}</td>
                  <td className="py-2.5 px-4 text-[10px]"><span className={`px-2 py-0.5 rounded font-bold ${risk.includes("High") ? "bg-red-50 text-red-700" : risk.includes("Medium") ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{risk}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 bg-amber-50 border-t border-amber-100 text-[10px] text-amber-700">
            Scenarios reflect Nigerian industrial and infrastructure realities. Biomass feedstock not guaranteed. Solar conditional on grid stability. Carbon credit pathways subject to methodology and registry requirements.
          </div>
        </div>
      )}

          {/* PROJECT RANKING */}
      {activeTab === "projects" && (
        <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-700">Projects Ranked by Readiness × Reduction ÷ Payback</p>
            <p className="text-[9px] text-slate-400 mt-0.5">Bankability = Readiness+ICMA+Low complexity. Not-financeable = readiness&lt;40 or payback&gt;15yr.</p>
          </div>
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["#","Project","CAPEX","Reduction","Payback","Readiness","Complexity","Bankability","Status"].map(h => (
                <th key={h} className="text-left py-2 px-3 text-slate-500 font-semibold text-[11px]">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {projData.map((p, i) => {
                const bankable = p.readiness_score >= 65 && (p.icma_eligible === 1 || p.icma_eligible === true);
                const needsWork = p.readiness_score >= 40 && !bankable;
                const bStyle = bankable ? "bg-emerald-50 text-emerald-700" : needsWork ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700";
                const bLabel = bankable ? "Bankable" : needsWork ? "Needs work" : "Not ready";
                return (
                  <tr key={p.project_id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 px-3 font-black text-slate-400 text-[10px]">#{i+1}</td>
                    <td className="py-2 px-3 font-medium max-w-[150px]"><p className="truncate text-[10px]">{p.name}</p></td>
                    <td className="py-2 px-3 font-bold text-blue-700 text-[10px]">{fmtU(p.capex_usd)}</td>
                    <td className="py-2 px-3 text-emerald-700 font-bold text-[10px]">{fmtN(p.reduction_tco2e_yr)}</td>
                    <td className="py-2 px-3 font-bold text-[10px]">{p.simple_payback_yrs}yr</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5">
                        <div className="w-10 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: p.readiness_score+"%" }}/>
                        </div>
                        <span className="font-bold text-[10px]">{p.readiness_score}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${p.complexity==="Low"?"bg-emerald-50 text-emerald-700":p.complexity==="Medium"?"bg-amber-50 text-amber-700":"bg-red-50 text-red-700"}`}>{p.complexity}</span>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${bStyle}`}>{bLabel}</span>
                    </td>
                    <td className="py-2 px-3 capitalize text-slate-500 text-[10px]">{p.status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
